import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Instagram } from "lucide-react";
import { useLang } from "@/i18n";
import api from "@/lib/api";

/**
 * Instagram feed rendered from admin-curated post URLs.
 * Uses Instagram's official embed.js — no API keys, no OAuth.
 * Displays as a horizontal snap-carousel with arrow controls and dot indicators.
 */
export default function InstagramFeed() {
  const { t, tt, isRTL } = useLang();
  const [posts, setPosts] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [autoplaySec, setAutoplaySec] = useState(0);
  const [paused, setPaused] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    api
      .get("/instagram_posts")
      .then((r) => setPosts(r.data || []))
      .catch(() => {});
    api
      .get("/settings")
      .then((r) => {
        const map = Object.fromEntries((r.data || []).map((s) => [s.key, s.value]));
        const n = Number(map.instagram_autoplay_seconds || 0);
        setAutoplaySec(Number.isFinite(n) && n >= 2 ? Math.min(30, n) : 0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!posts.length) return;

    const processEmbeds = () => {
      if (window.instgrm && window.instgrm.Embeds) {
        try {
          window.instgrm.Embeds.process();
        } catch (_) {
          /* ignore */
        }
      }
    };

    if (!document.querySelector('script[data-ig-embed="1"]')) {
      const s = document.createElement("script");
      s.src = "https://www.instagram.com/embed.js";
      s.async = true;
      s.defer = true;
      s.setAttribute("data-ig-embed", "1");
      s.onload = processEmbeds;
      document.body.appendChild(s);
    } else {
      const tm = setTimeout(processEmbeds, 60);
      return () => clearTimeout(tm);
    }
  }, [posts]);

  const updateArrowState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 8);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 8);
    // Which card is closest to the left edge?
    const card = el.querySelector("[data-ig-card]");
    if (card) {
      const cardWidth = card.getBoundingClientRect().width + 24; // approx gap
      const idx = Math.round(scrollLeft / Math.max(1, cardWidth));
      setActiveIdx(Math.max(0, Math.min(posts.length - 1, idx)));
    }
  }, [posts.length]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateArrowState();
    const onScroll = () => updateArrowState();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateArrowState);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateArrowState);
    };
  }, [updateArrowState, posts.length]);

  const scrollByOne = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-ig-card]");
    const step = card ? card.getBoundingClientRect().width + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const scrollToIdx = (idx) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelectorAll("[data-ig-card]")[idx];
    if (card) card.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
  };

  // Autoplay: advance to the next card every `autoplaySec` seconds when
  // enabled, not paused (hover/focus/prefers-reduced-motion) and the tab is
  // visible. If we're at the end, wrap back to the beginning.
  useEffect(() => {
    if (!autoplaySec || !posts.length || paused) return;
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;

    const id = setInterval(() => {
      if (document.hidden) return;
      const el = scrollerRef.current;
      if (!el) return;
      const cards = el.querySelectorAll("[data-ig-card]");
      if (!cards.length) return;
      const next = (activeIdx + 1) % cards.length;
      const target = cards[next];
      if (target) target.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
    }, autoplaySec * 1000);
    return () => clearInterval(id);
  }, [autoplaySec, activeIdx, posts.length, paused]);

  if (!posts.length) return null;

  const prevDir = isRTL ? 1 : -1;
  const nextDir = isRTL ? -1 : 1;

  return (
    <section
      className="max-w-7xl mx-auto px-6 md:px-10 py-24"
      data-testid="instagram-feed-section"
    >
      <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.35em] text-brand-orange font-semibold mb-3 flex items-center gap-2">
            <Instagram size={12} className="inline mb-0.5" /> Instagram
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-brand-navy max-w-2xl">
            {tt({
              tr: "Instagram'dan Kareler",
              ar: "من إنستقرام",
              en: "From our Instagram",
              ru: "Из нашего Instagram",
            })}
          </h2>
          <p className="mt-3 text-slate-600 max-w-2xl">
            {tt({
              tr: "Mutfağımızdan güncel paylaşımlar — direkt Instagram akışımızdan.",
              ar: "أحدث مشاركاتنا من مطبخنا — مباشرةً من حسابنا على إنستقرام.",
              en: "Fresh moments from our kitchen — straight from our Instagram feed.",
              ru: "Свежие моменты с нашей кухни — прямо из ленты Instagram.",
            })}
          </p>
        </div>

        <div className="flex items-center gap-2" data-testid="ig-carousel-controls">
          <button
            type="button"
            onClick={() => scrollByOne(prevDir)}
            disabled={isRTL ? !canScrollRight : !canScrollLeft}
            aria-label="Previous"
            data-testid="ig-carousel-prev"
            className="w-11 h-11 rounded-full border border-brand-navy/20 bg-white text-brand-navy hover:bg-brand-navy hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition flex items-center justify-center shadow-sm"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={() => scrollByOne(nextDir)}
            disabled={isRTL ? !canScrollLeft : !canScrollRight}
            aria-label="Next"
            data-testid="ig-carousel-next"
            className="w-11 h-11 rounded-full border border-brand-navy/20 bg-white text-brand-navy hover:bg-brand-navy hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition flex items-center justify-center shadow-sm"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <div
        className="relative"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={() => setPaused(false)}
      >
        {/* edge fade */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-canvas-cream to-transparent z-10" aria-hidden="true" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-canvas-cream to-transparent z-10" aria-hidden="true" />

        <div
          ref={scrollerRef}
          className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 -mx-2 px-2 ig-scroller"
          data-testid="instagram-feed-carousel"
          style={{ scrollbarWidth: "thin" }}
        >
          {posts.map((p) => (
            <div
              key={p.id}
              data-ig-card
              className="snap-start shrink-0 w-[85%] sm:w-[60%] md:w-[45%] lg:w-[32%] premium-card rounded-2xl overflow-hidden bg-white border border-brand-gold/20 shadow-sm p-1 min-h-[520px]"
              data-testid={`instagram-post-${p.id}`}
            >
              <blockquote
                className="instagram-media"
                data-instgrm-permalink={p.url}
                data-instgrm-version="14"
                style={{
                  background: "#FFF",
                  border: 0,
                  margin: "1px",
                  padding: 0,
                  minWidth: "260px",
                  width: "calc(100% - 2px)",
                }}
              >
                <a href={p.url} target="_blank" rel="noopener noreferrer">
                  {p.caption || t("nav.gallery")}
                </a>
              </blockquote>
            </div>
          ))}
        </div>

        {posts.length > 1 && (
          <div className="flex justify-center gap-2 mt-4" data-testid="ig-carousel-dots">
            {posts.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => scrollToIdx(i)}
                data-testid={`ig-carousel-dot-${i}`}
                className={`h-2 rounded-full transition-all ${
                  i === activeIdx
                    ? "w-8 bg-brand-orange"
                    : "w-2 bg-brand-navy/25 hover:bg-brand-navy/50"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
